import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { ArrowLeft, CheckCircle2, Download, FileText } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Textarea } from '@/components/ui/textarea'
import { Input } from '@/components/ui/input'
import { LoadingSpinner } from '@/components/ui/LoadingSpinner'
import { QuizAnswerPreview } from '@/components/quizzes/QuizAnswerPreview'
import {
  useQuiz,
  useQuizAttempt,
  useQuizQuestions,
  useGradeQuizAttempt,
} from '@/hooks/useQuizzes'
import { useUser } from '@/hooks/useUsers'
import { ManualGradeEntry, QuizQuestion } from '@/types/quizzes'
import { QuizAttemptModel } from '@/models/quiz.model'
import { paths } from '@/routes/path'
import { labelForQuizAttemptStatus, labelForQuizQuestionType } from '@/utils/quiz'

type ManualScoreState = Record<
  string,
  {
    manualScore: number | null
    feedback: string
  }
>

export function QuizAttemptDetailPage() {
  const { quizId, attemptId } = useParams<{ quizId: string; attemptId: string }>()
  const navigate = useNavigate()

  const { data: quiz, isLoading: isQuizLoading } = useQuiz(quizId)
  const { data: attempt, isLoading: isAttemptLoading, error: attemptError } = useQuizAttempt(
    quizId,
    attemptId,
  )
  const { data: quizQuestions, isLoading: isQuestionsLoading } = useQuizQuestions(quizId)
  const gradeAttempt = useGradeQuizAttempt(quizId)
  const learnerUserId = attempt?.learnerId
  const { data: learnerProfile } = useUser(learnerUserId)

  const [gradingState, setGradingState] = useState<ManualScoreState>({})

  const questionMap = useMemo(() => {
    const questions = quizQuestions?.questions ?? quiz?.questions ?? []
    return questions.reduce<Record<string, QuizQuestion>>((acc, question) => {
      acc[question.id] = question
      return acc
    }, {})
  }, [quizQuestions, quiz])

  const attemptEntries = useMemo(() => {
    if (!attempt) return []
    const answers = attempt.answers?.questions ?? []
    return answers.map((answer) => {
      const question = questionMap[answer.id]
      const scoreRecord = attempt.scores?.find((entry) => entry.questionId === answer.id)
      return {
        question,
        answer,
        scoreRecord,
      }
    })
  }, [attempt, questionMap])

  const initializeState = () => {
    if (!attempt) return
    const current: ManualScoreState = {}
    attempt.scores?.forEach((score) => {
      current[score.questionId] = {
        manualScore:
          score.manualScore !== null && score.manualScore !== undefined
            ? Number(score.manualScore)
            : null,
        feedback: score.feedback ?? '',
      }
    })
    setGradingState(current)
  }

  useEffect(() => {
    initializeState()
  }, [attempt])

  if (!quizId || !attemptId) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Attempt not found</CardTitle>
            <CardDescription className="text-sm text-red-600">
              We could not identify the attempt you want to review.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  if (isQuizLoading || isAttemptLoading || isQuestionsLoading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (attemptError || !attempt || !quiz) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Card className="max-w-lg border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-700">Unable to load attempt</CardTitle>
            <CardDescription className="text-sm text-red-600">
              The attempt may have been removed or you may not have permission to view it.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  const learnerDisplayName = (() => {
    const inlineName = attempt.learnerName?.trim()
    if (inlineName) return inlineName
    if (attempt.learner?.email) return attempt.learner.email
    const profileName = learnerProfile?.fullName?.trim()
    if (profileName) return profileName
    if (learnerProfile?.email) return learnerProfile.email
    return attempt.learnerId
  })()

  const learnerSecondaryLabel = attempt.learner?.email ?? learnerProfile?.email ?? null

  const handleManualScoreChange = (questionId: string, value: string) => {
    setGradingState((prev) => ({
      ...prev,
      [questionId]: {
        manualScore: value === '' ? null : Number(value),
        feedback: prev[questionId]?.feedback ?? '',
      },
    }))
  }

  const handleFeedbackChange = (questionId: string, value: string) => {
    setGradingState((prev) => ({
      ...prev,
      [questionId]: {
        manualScore: prev[questionId]?.manualScore ?? null,
        feedback: value,
      },
    }))
  }

  const handleSubmit = async () => {
    const manualScores: ManualGradeEntry[] = []

    attemptEntries.forEach(({ answer, scoreRecord }) => {
      const entry = gradingState[answer.id]
      const existingScore =
        scoreRecord?.manualScore !== null && scoreRecord?.manualScore !== undefined
          ? Number(scoreRecord.manualScore)
          : null
      const manualScoreValue =
        entry?.manualScore !== null &&
        entry?.manualScore !== undefined &&
        !Number.isNaN(entry.manualScore)
          ? Number(entry.manualScore)
          : existingScore
      const feedbackValue = entry?.feedback?.trim() ?? scoreRecord?.feedback ?? ''

      if (manualScoreValue === null && !feedbackValue) {
        return
      }

      manualScores.push({
        questionId: answer.id,
        manualScore: manualScoreValue ?? 0,
        feedback: feedbackValue || undefined,
      })
    })

    await gradeAttempt.mutateAsync({
      quizId,
      attemptId,
      manualScores,
      status: 'graded',
    })
    toast.success('Manual grading saved.')
    if (quizId && attemptId) {
      navigate(paths.app.quizzes.attempts.detail.build(quizId, attemptId), { replace: true })
    }
  }

  return (
    <div className="space-y-8">
      <header className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="space-y-2">
          <Button
            variant="ghost"
            className="gap-2 p-0 text-sm text-gray-600"
                      onClick={() => navigate(paths.app.quizzes.attempts.list.build(quiz.id))}
          >
            <ArrowLeft className="h-4 w-4" />
            Back to attempts
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">{quiz.title}</h1>
          <p className="text-gray-600">
            Review answers, adjust manual scores, and leave feedback the learner can act on.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline">Attempt {attempt.attempt}</Badge>
          <Badge variant="secondary">{labelForQuizAttemptStatus(attempt.status)}</Badge>
          <Badge variant="outline">
            Started {attempt.startedAt ? new Date(attempt.startedAt).toLocaleString() : 'Unknown'}
          </Badge>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Learner details</CardTitle>
          <CardDescription>
            Keep this information handy if you need to follow up with the learner.
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <InfoItem
            label="Learner"
            value={
              <div className="space-y-1">
                <p className="font-semibold text-gray-900">{learnerDisplayName}</p>
                {learnerSecondaryLabel && (
                  <p className="text-xs text-gray-600">{learnerSecondaryLabel}</p>
                )}
                <p className="font-mono text-xs text-gray-500">ID: {attempt.learnerId}</p>
              </div>
            }
          />
          <InfoItem
            label="Submitted at"
            value={attempt.submittedAt ? new Date(attempt.submittedAt).toLocaleString() : 'Not yet submitted'}
          />
          <InfoItem
            label="Time spent"
            value={
              attempt.durationSeconds
                ? `${Math.round(attempt.durationSeconds / 60)} minutes`
                : 'Unknown'
            }
          />
          <InfoItem label="IP address" value={attempt.ipAddress ?? 'Unavailable'} />
          <InfoItem label="Device" value={attempt.answers?.meta?.device ?? 'Unknown'} />
          <InfoItem label="User agent" value={attempt.userAgent ?? attempt.answers?.meta?.userAgent ?? 'Unknown'} />
        </CardContent>
      </Card>

      <section className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Responses</h2>
          <div className="flex items-center gap-3">
            <Badge variant="secondary">
              Auto score: {attempt.autoScore !== null ? attempt.autoScore?.toFixed(2) : 'N/A'}
            </Badge>
            <Badge variant="secondary">
              Manual score: {attempt.manualScore !== null ? attempt.manualScore?.toFixed(2) : 'Not graded'}
            </Badge>
          </div>
        </div>

        <div className="space-y-4">
          {attemptEntries.map(({ question, answer, scoreRecord }) => (
            <Card key={answer.id} className="border border-gray-200">
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-base">
                  <span>
                    {question?.questionText ?? 'Question details unavailable'}
                  </span>
                  <Badge variant="outline">
                    {question ? labelForQuizQuestionType(question.type) : 'Unknown type'}
                  </Badge>
                </CardTitle>
                <CardDescription>
                  Auto score: {scoreRecord?.autoScore ?? 'N/A'}{' '}
                  | Manual score:{' '}
                  {gradingState[answer.id]?.manualScore ?? scoreRecord?.manualScore ?? 'Not graded'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <QuizAnswerPreview question={question} answer={answer.answer} files={attempt.files} />
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <label htmlFor={`manualScore-${answer.id}`} className="text-sm font-medium text-gray-700">
                      Manual score
                    </label>
                    <Input
                      id={`manualScore-${answer.id}`}
                      type="number"
                      step="0.5"
                      min={0}
                      value={
                        gradingState[answer.id]?.manualScore ??
                        (scoreRecord?.manualScore !== null && scoreRecord?.manualScore !== undefined
                          ? Number(scoreRecord.manualScore)
                          : '')
                      }
                      onChange={(event) => handleManualScoreChange(answer.id, event.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <label htmlFor={`feedback-${answer.id}`} className="text-sm font-medium text-gray-700">
                      Feedback for learner
                    </label>
                    <Textarea
                      id={`feedback-${answer.id}`}
                      rows={3}
                      placeholder="Share guidance or encouragement to help the learner improve."
                      value={
                        gradingState[answer.id]?.feedback ??
                        scoreRecord?.feedback ??
                        ''
                      }
                      onChange={(event) => handleFeedbackChange(answer.id, event.target.value)}
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      <div className="flex items-center justify-end gap-3">
        <Button
          variant="outline"
          onClick={() => navigate(paths.app.quizzes.attempts.list.build(quiz.id))}
        >
          Cancel
        </Button>
        <Button
          className="flex items-center gap-2"
          onClick={handleSubmit}
          disabled={gradeAttempt.isPending}
        >
          <CheckCircle2 className="h-4 w-4" />
          {gradeAttempt.isPending ? 'Saving...' : 'Save grading'}
        </Button>
      </div>
    </div>
  )
}

function InfoItem({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-1 rounded-lg border border-gray-200 bg-gray-50 p-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <div className="text-sm text-gray-800">{value}</div>
    </div>
  )
}
